import { Button, Result, Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'
import TeamAuditTab from './TeamAuditTab'
import TeamCreditsTab from './TeamCreditsTab'
import TeamMemberProjectsTab from './TeamMemberProjectsTab'
import TeamMembersTab from './TeamMembersTab'
import TeamOverviewTab from './TeamOverviewTab'
import TeamPermissionsTab from './TeamPermissionsTab'
import TeamSettingsTab from './TeamSettingsTab'
import TeamUsageTab from './TeamUsageTab'
import { canViewMemberProjects, resolveTeamTab, visibleTeamTabs } from './teamPermissions'
import { resolveTeamPlanBadge } from './teamPlanBadge'
import { useWorkspaceState } from '../WorkspaceContext'
import './team-management.css'

/**
 * VistoeraTASK-00525 — 团队管理 page shell (Canonical UI Reference: the three
 * confirmed designs). All seven tabs are live: phase 1 (概览 / 成员 / 设置),
 * phase 2 (积分 / 使用统计) and phase 3 (权限管理 / 操作日志).
 * VistoeraTASK-00670 — title-row plan badge from Workspace Effective Plan.
 */
export default function TeamManagementPage() {
  const { workspaceId = '' } = useParams()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation('team')
  const base = `/workspace/${workspaceId}/team`
  const active = resolveTeamTab(pathname, base)
  const { workspace, loading, error, reload } = useWorkspaceState()
  const isOwner = canViewMemberProjects(workspace?.my_role)
  const planBadge = resolveTeamPlanBadge({
    loading,
    loadError: !!error,
    planKey: workspace?.plan_key,
    planDisplayName: workspace?.plan_display_name,
  })

  return (
    <div className="tm-page" data-testid="team-management-page">
      <header className="tm-page-head">
        <div className="tm-title-row">
          <h1 className="tm-title">{t('page.title')}</h1>
          <span
            className={`tm-plan-badge is-${planBadge.state}`}
            data-testid="team-plan-badge"
            data-state={planBadge.state}
            title={planBadge.state === 'error' ? t('page.planRetryHint') : undefined}
          >
            {planBadge.state === 'error' ? (
              <button type="button" className="tm-plan-badge-retry" onClick={() => reload()}>
                {planBadge.label}
                <span className="tm-plan-badge-retry-label">{t('common.retry')}</span>
              </button>
            ) : (
              planBadge.label
            )}
          </span>
        </div>
        <p className="tm-subtitle">{t('page.subtitle')}</p>
      </header>

      <nav className="tm-tabs" role="tablist" aria-label={t('page.title')}>
        {visibleTeamTabs(workspace?.my_role).map((tab) => {
          const label = t(`tabs.${tab.key}`)
          if (!tab.enabled) {
            return (
              <Tooltip key={tab.key} title={t('tabs.comingSoon')}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={false}
                  aria-disabled="true"
                  disabled
                  className="tm-tab is-disabled"
                  data-testid={`team-tab-${tab.key}`}
                >
                  {label}
                  <span className="tm-tab-soon">{t('tabs.comingSoonShort')}</span>
                </button>
              </Tooltip>
            )
          }
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={active === tab.key}
              className={`tm-tab${active === tab.key ? ' is-active' : ''}`}
              data-testid={`team-tab-${tab.key}`}
              onClick={() => navigate(tab.path ? `${base}/${tab.path}` : base)}
            >
              {label}
            </button>
          )
        })}
      </nav>

      <div className="tm-body">
        <Routes>
          <Route index element={<TeamOverviewTab workspaceId={workspaceId} />} />
          <Route path="members" element={<TeamMembersTab workspaceId={workspaceId} />} />
          <Route path="credits" element={<TeamCreditsTab workspaceId={workspaceId} />} />
          <Route path="permissions" element={<TeamPermissionsTab workspaceId={workspaceId} />} />
          <Route path="usage" element={<TeamUsageTab workspaceId={workspaceId} />} />
          <Route path="settings" element={<TeamSettingsTab workspaceId={workspaceId} />} />
          <Route
            path="member-projects"
            element={
              isOwner ? (
                <TeamMemberProjectsTab workspaceId={workspaceId} />
              ) : (
                <Result
                  status="403"
                  title={t('guard.title')}
                  subTitle={t('guard.ownerOnly')}
                  extra={
                    <Button type="primary" onClick={() => navigate(base, { replace: true })}>
                      {t('guard.back')}
                    </Button>
                  }
                />
              )
            }
          />
          <Route path="logs" element={<TeamAuditTab workspaceId={workspaceId} />} />
          <Route path="*" element={<Navigate to={base} replace />} />
        </Routes>
      </div>
    </div>
  )
}
